import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Card, CardContent } from "@/shared/components/ui/card";
import type { ServerStatus } from "@/shared/stores/serversStore";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import { useToast } from "@/shared/hooks/use-toast";
import {
    Blocks,
    Copy,
    Globe,
    Rocket,
    Skull,
    Users,
    Wifi,
} from "lucide-react";

interface ServerGridProps {
    servers: ServerStatus[];
    loading?: boolean;
    error?: string | null;
}

const _accentByGame = (game: string): string => {
    switch ((game || "").toLowerCase()) {
        case "zomboid":
            return "from-emerald-500/35 via-lime-400/25 to-cyan-300/25";
        case "cobblemon":
            return "from-orange-500/35 via-yellow-400/25 to-red-400/25";
        case "rimworld":
            return "from-sky-500/35 via-blue-500/25 to-indigo-400/25";
        case "ksp":
            return "from-fuchsia-500/35 via-pink-500/25 to-rose-400/25";
        default:
            return "from-primary/40 via-orange-400/30 to-amber-300/25";
    }
};

const _iconByGame = (game: string) => {
    switch ((game || "").toLowerCase()) {
        case "zomboid":
            return Skull;
        case "cobblemon":
            return Blocks;
        case "ksp":
            return Rocket;
        default:
            return Wifi;
    }
};

/**
 * Strip Zomboid-specific markup tags from server descriptions.
 * Converts <LINE> → newline, <SPACE> → space, removes <RGB:...> color tags,
 * and cleans surrounding quotes.
 */
const _stripZomboidMarkup = (raw: string): string => {
    if (!raw) return "";
    let text = raw;
    // Remove surrounding quotes
    text = text.replace(/^"|"$/g, "");
    // Convert markup to readable text
    text = text.replace(/<LINE>/gi, "\n");
    text = text.replace(/<SPACE>/gi, " ");
    // Remove <RGB:x,y,z> color tags
    text = text.replace(/<RGB:[^>]*>/gi, "");
    // Collapse multiple spaces
    text = text.replace(/ {2,}/g, " ");
    // Trim each line
    text = text
        .split("\n")
        .map((l) => l.trim())
        .filter((l) => l.length > 0)
        .join("\n");
    return text.trim();
};

const _formatEndpoint = (server: ServerStatus): string => {
    const endpoint = (server.domain || server.ip || "").trim();
    if (!endpoint) return "sconosciuto";
    if (server.port) return `${endpoint}:${server.port}`;
    return endpoint;
};

async function _copyToClipboard(text: string): Promise<boolean> {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch {
        try {
            const el = document.createElement("textarea");
            el.value = text;
            el.style.position = "fixed";
            el.style.opacity = "0";
            document.body.appendChild(el);
            el.focus();
            el.select();
            const ok = document.execCommand("copy");
            document.body.removeChild(el);
            return ok;
        } catch {
            return false;
        }
    }
}

export const ServerGrid: React.FC<ServerGridProps> = ({
    servers,
    loading = false,
    error = null,
}) => {
    const { toast } = useToast();

    return (
        <section className="relative px-4 py-16 sm:py-20 lg:py-24">
            <div className="mx-auto max-w-7xl">
                <motion.div
                    className="mb-10 text-center"
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true }}
                >
                    <p className="font-arcade text-xs uppercase tracking-[0.45em] text-orange-500">
                        Stato live
                    </p>
                    <h2 className="mt-4 font-gaming text-4xl sm:text-5xl">
                        Mondi attivi
                    </h2>
                    <p className="mt-3 text-base text-muted-foreground sm:text-lg">
                        Mostriamo solo server realmente online in questo momento.
                    </p>
                </motion.div>

                <AnimatePresence>
                    {servers.length > 0 && (
                        <motion.div
                            className="grid grid-cols-1 gap-6 lg:grid-cols-2"
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            transition={{ duration: 0.5 }}
                            viewport={{ once: true }}
                        >
                            {servers.map((server, index) => {
                                const currentPlayers =
                                    Number(server.players?.current || 0);
                                const maxPlayers = Number(server.players?.max || 0);
                                const loadPercent =
                                    maxPlayers > 0
                                        ? Math.min(
                                            100,
                                            (currentPlayers / maxPlayers) * 100,
                                        )
                                        : 0;
                                const endpoint = _formatEndpoint(server);
                                const GameIcon = _iconByGame(server.game);

                                return (
                                    <motion.div
                                        key={server.id}
                                        initial={{ opacity: 0, y: 26 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        transition={{
                                            duration: 0.45,
                                            delay: index * 0.08,
                                        }}
                                        viewport={{ once: true }}
                                    >
                                        <Card className="group relative overflow-hidden border-border/70 bg-card/70 backdrop-blur-xl">
                                            <div
                                                className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${_accentByGame(
                                                    server.game,
                                                )} opacity-45 transition-opacity duration-300 group-hover:opacity-55`}
                                            />
                                            <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100 [background:linear-gradient(to_bottom,transparent,rgba(255,255,255,0.08),transparent)] [background-size:100%_26px] [mask-image:linear-gradient(to_bottom,transparent,black,transparent)]" />
                                            <div className="pointer-events-none absolute -right-20 -top-16 h-52 w-52 rounded-full bg-white/20 blur-3xl dark:bg-white/10" />

                                            <CardContent className="relative p-6">
                                                <div className="flex items-start justify-between gap-4">
                                                    <div className="min-w-0">
                                                        <div className="flex items-center gap-2">
                                                            <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-border/60 bg-background/40 backdrop-blur">
                                                                <GameIcon className="h-5 w-5 text-orange-500" />
                                                            </span>
                                                            <div className="min-w-0">
                                                                <p className="truncate font-arcade text-[11px] uppercase tracking-[0.25em] text-orange-500">
                                                                    {server.game}
                                                                </p>
                                                                <h3 className="truncate font-gaming text-2xl">
                                                                    {server.name}
                                                                </h3>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <Badge className="border-0 bg-emerald-500/20 px-3 py-1 text-emerald-900 dark:text-emerald-100">
                                                        <span className="mr-2 inline-flex h-2 w-2 animate-pulse rounded-full bg-emerald-500" />
                                                        online
                                                    </Badge>
                                                </div>

                                                {server.description && (() => {
                                                    const clean = _stripZomboidMarkup(server.description);
                                                    return clean ? (
                                                        <p className="mt-3 whitespace-pre-line text-sm text-muted-foreground">
                                                            {clean}
                                                        </p>
                                                    ) : null;
                                                })()}

                                                <div className="mt-5 grid gap-3 sm:grid-cols-2">
                                                    <div className="rounded-xl border border-border/70 bg-background/55 p-3">
                                                        <p className="mb-2 inline-flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
                                                            <Users className="h-3.5 w-3.5" />
                                                            Giocatori
                                                        </p>
                                                        <p className="font-gaming text-xl">
                                                            {currentPlayers}/{maxPlayers}
                                                        </p>
                                                        <div className="mt-2 h-2 overflow-hidden rounded-full bg-muted">
                                                            <motion.div
                                                                className="h-full rounded-full bg-gradient-to-r from-orange-500 to-amber-400"
                                                                initial={{ width: 0 }}
                                                                whileInView={{
                                                                    width: `${loadPercent}%`,
                                                                }}
                                                                transition={{
                                                                    duration: 0.8,
                                                                    delay: 0.1,
                                                                }}
                                                                viewport={{ once: true }}
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="rounded-xl border border-border/70 bg-background/55 p-3">
                                                        <p className="mb-2 inline-flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
                                                            <Globe className="h-3.5 w-3.5" />
                                                            Endpoint
                                                        </p>
                                                        <div className="flex items-center gap-2">
                                                            <p className="min-w-0 flex-1 truncate font-mono text-sm text-cyan-700 dark:text-cyan-200">
                                                                {endpoint}
                                                            </p>
                                                            <Button
                                                                type="button"
                                                                size="icon"
                                                                variant="ghost"
                                                                className="h-8 w-8 border border-border/60 bg-background/40 hover:bg-background/70"
                                                                onClick={async () => {
                                                                    const ok = await _copyToClipboard(endpoint);
                                                                    toast({
                                                                        title: ok
                                                                            ? "Copiato"
                                                                            : "Copia non riuscita",
                                                                        description: ok
                                                                            ? endpoint
                                                                            : "Copia manualmente l'endpoint.",
                                                                    });
                                                                }}
                                                            >
                                                                <Copy className="h-4 w-4" />
                                                                <span className="sr-only">
                                                                    Copia endpoint
                                                                </span>
                                                            </Button>
                                                        </div>
                                                        <p className="mt-1 text-xs text-muted-foreground">
                                                            ID: {server.id}
                                                        </p>
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    </motion.div>
                                );
                            })}
                        </motion.div>
                    )}
                </AnimatePresence>

                <AnimatePresence>
                    {servers.length === 0 && (
                        <motion.div
                            className="rounded-2xl border border-border/70 bg-card/60 p-8 text-center backdrop-blur-xl"
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -16 }}
                        >
                            <h3 className="font-gaming text-2xl">
                                {loading
                                    ? "Sincronizzazione in corso"
                                    : error
                                        ? "Errore nel caricamento"
                                        : "Nessun server online"}
                            </h3>
                            <p className="mt-2 text-sm text-muted-foreground">
                                {loading
                                    ? "Sto leggendo lo stato dei server in tempo reale."
                                    : error ||
                                    "I server torneranno disponibili a breve."}
                            </p>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </section>
    );
};
